import type { AxiosRequestConfig } from 'axios';
import { triviaClient, worldplayClient } from '../client';
import { brand } from 'brands';
import { setTriviaTokenFromResponse } from 'functions/triviaTokenStorage';
import type { ExtendedAxiosRequestConfig } from '../../types/common';
import type { DcbUserResponse } from '../../types/dcb';
import type {
    WorldplayDoiLoginResponse,
    WorldplaySubscribeResponse,
    WorldplayUser,
} from '../../types/worldplay';

const logAndThrow = (label: string, error: unknown): never => {
    console.error(`[${label} error]:`, error);
    throw error;
};

const SUBSCRIBED = ['active', 'grace'];
const FREE = ['interested', 'pending'];

/**
 * Maps a WorldPlay status onto the client's: 'Active' is a subscriber,
 * 'Pending' a free player, and anything else has to subscribe again.
 * Only the first word counts, so 'Active Subscription' is 'Active'.
 */
export function toClientStatus(status: string | null | undefined): string {
    const word = String(status ?? '')
        .trim()
        .split(/\s+/)[0]
        .toLowerCase();
    if (SUBSCRIBED.includes(word)) return 'Active';
    if (FREE.includes(word)) return 'Pending';
    return 'Cancelled';
}

/**
 * Reshapes a WorldPlay user into the user object the rest of the app reads.
 * `serviceId` is what trivia calls send as `service`, so it is the brand's
 * trivia service (WorldPlay's own Service name as a fallback).
 */
export function toClientUser(user: WorldplayUser): DcbUserResponse {
    return {
        msisdn: String(user.Msisdn),
        serviceId: brand.trivia.service ?? user.Service ?? undefined,
        status: toClientStatus(user.Status),
        username: user.Username ?? null,
        subscriptionId: user.SubscriptionId == null ? null : String(user.SubscriptionId),
        productId: user.ProductId,
    };
}

export default class WorldplayService {
    static async getUser(msisdn: string, productId: number): Promise<DcbUserResponse | undefined> {
        try {
            const response = await worldplayClient.post<WorldplayUser | null>('/get-user', {
                msisdn,
                productId,
            });
            // Failures come back as 200 too: null, or { message, description }.
            if (!response.data?.Msisdn) {
                throw new Error(`WorldPlay returned no user: ${JSON.stringify(response.data)}`);
            }
            return toClientUser(response.data);
        } catch (error) {
            logAndThrow('WorldPlay Get User', error);
        }
    }

    /**
     * Forced-DOI subscribe: WorldPlay asks the subscriber to confirm on their
     * phone, and its callback later flips them Active (poll getUser for that).
     */
    static async subscribe(
        service: string,
        msisdn: string
    ): Promise<WorldplaySubscribeResponse | undefined> {
        try {
            const response = await worldplayClient.post<WorldplaySubscribeResponse>(
                '/check-mark-status',
                { service, msisdn }
            );
            return response.data;
        } catch (error) {
            logAndThrow('WorldPlay Subscribe', error);
        }
    }

    /**
     * Logs in a subscriber returning from MTN's DOI page. trivia-v2 issues the
     * token only if `subscriptionRef` (the page's `ai` param) matches the
     * subscription WorldPlay reported to us directly, so the URL alone is not
     * enough. Failures return without redirecting, as the caller retries.
     */
    static async doiLogin(
        msisdn: string,
        productId: number,
        subscriptionRef: string
    ): Promise<WorldplayDoiLoginResponse | undefined> {
        const config: AxiosRequestConfig & ExtendedAxiosRequestConfig = {
            skipAuthRedirect: true,
            skipServerErrorRedirect: true,
        };
        const response = await triviaClient.post<WorldplayDoiLoginResponse>(
            '/worldplay/doi-login',
            { msisdn, productId, subscriptionRef },
            config
        );
        setTriviaTokenFromResponse(response.data);
        return response.data;
    }

    /**
     * Logs in the subscriber MTN identified by header enrichment, from the
     * e-msisdn service.mtn-header-enrichment sent them back with. trivia-v2
     * decrypts it (through DEP) and issues the token only when WorldPlay has the
     * number as Active; anyone else gets { verified: false }. Failures return
     * without redirecting, so the caller can fall back to OTP.
     */
    static async heLogin(
        encryptedMsisdn: string,
        productId: number
    ): Promise<WorldplayDoiLoginResponse | undefined> {
        const config: AxiosRequestConfig & ExtendedAxiosRequestConfig = {
            skipAuthRedirect: true,
            skipServerErrorRedirect: true,
        };
        const response = await triviaClient.post<WorldplayDoiLoginResponse>(
            '/worldplay/he-login',
            { encryptedMsisdn, productId },
            config
        );
        setTriviaTokenFromResponse(response.data);
        return response.data;
    }

    static async updateUsername(
        msisdn: string,
        productId: number,
        username: string
    ): Promise<unknown | undefined> {
        try {
            const response = await worldplayClient.post('/update-username', {
                msisdn,
                productId,
                username,
            });
            return response.data;
        } catch (error) {
            logAndThrow('WorldPlay Update Username', error);
        }
    }
}
